import React, { useRef } from 'react';
import { SafeForm } from '../SafeForm';
import { Sparkles, Loader2, ImagePlus, MessageSquareText } from 'lucide-react';
import { fileUrl } from './useVisualizer';

const Chips = ({ id, items, value, onChange, testPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((o) => (
      <button key={o.id} type="button" onClick={() => onChange(id, value === o.id ? null : o.id)}
        className={`px-3.5 py-2 rounded-full text-sm border transition-colors ${value === o.id ? 'bg-[#3a190b] text-white border-[#3a190b]' : 'bg-white text-[#202020] border-[#3a190b]/20 hover:border-[#3a190b]'}`}
        data-testid={`${testPrefix}-${o.id}`} aria-pressed={value === o.id}>{o.label}</button>
    ))}
  </div>
);

const PLACEHOLDERS = {
  badkamer: 'bv. "Maak er een rustige badkamer van met een inloopdouche met zwarte kraan, grote beige tegels, zwevend houten meubel en een grote spiegel met verlichting."',
  keuken: 'bv. "Een witte keuken met kookeiland, houten accenten, keramisch werkblad en zwarte kranen. Raam behouden, meer licht."',
  woonkamer: 'bv. "Warme woonkamer met tegelparket, witte muren, een zwarte houtkachel en een grote boekenkast op maat rond de tv."',
  zolder: 'bv. "Maak van deze zolder een lichte slaapkamer met wit geschilderde balken, parketvloer en een dressing onder het schuine dak."',
  default: 'Beschrijf gewoon wat u wil, zoals u het aan ChatGPT zou vragen. Kleuren, materialen, sfeer, wat weg moet en wat moet blijven.',
};

const DesignOptions = ({ options, room, setRoom, choices, setChoices, extra, setExtra, onGenerate, busy, pending, remaining, session, onAddReference, refining }) => {
  const refInput = useRef(null);
  const disabled = busy || pending || remaining === 0 || !room;
  const groups = (options?.groups || []).filter((g) => !g.rooms || g.rooms.includes(room));
  const setChoice = (gid, v) => setChoices({ ...choices, [gid]: v });

  return (
    <SafeForm className="space-y-7" onValidSubmit={onGenerate} data-testid="design-options-form">
      <div>
        <h2 className="text-xl md:text-2xl font-bold text-[#202020] mb-3">{refining ? 'Pas uw ontwerp aan' : 'Stap 2 — Welke ruimte is dit?'}</h2>
        <Chips id="room" items={options?.rooms || []} value={room} onChange={(_, v) => { setRoom(v); setChoices({}); }} testPrefix="room" />
      </div>

      <div>
        <label htmlFor="design-prompt" className="flex items-center gap-2 text-sm font-semibold text-[#202020] mb-2"><MessageSquareText className="h-4 w-4 text-[#3a190b]" />Beschrijf wat u wil — in uw eigen woorden</label>
        <textarea id="design-prompt" value={extra} onChange={(e) => setExtra(e.target.value)} rows={4} maxLength={600}
          placeholder={PLACEHOLDERS[room] || PLACEHOLDERS.default}
          className="w-full rounded-xl border border-[#3a190b]/20 p-4 text-base focus:outline-none focus:border-[#3a190b] bg-white" data-testid="design-prompt-input" />
        <p className="text-xs text-[#202020]/50 mt-1">{extra.length}/600 · Hoe concreter, hoe beter het resultaat. Geen idee? Gebruik de snelle keuzes hieronder.</p>
      </div>

      {groups.length > 0 && (
        <details className="group rounded-xl border border-[#3a190b]/10 bg-white" open data-testid="quick-choices">
          <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-[#3a190b] flex justify-between">Snelle keuzes (optioneel): kleur, materiaal, vloer…<span className="text-[#3a190b]/50 group-open:rotate-180 transition-transform">⌄</span></summary>
          <div className="px-4 pb-4 space-y-4">
            {groups.map((g) => (
              <fieldset key={g.id} data-testid={`option-group-${g.id}`}>
                <legend className="text-xs uppercase tracking-wider font-semibold text-[#3a190b]/70 mb-2">{g.label}</legend>
                <Chips id={g.id} items={g.items} value={choices[g.id]} onChange={setChoice} testPrefix={`option-${g.id}`} />
              </fieldset>
            ))}
          </div>
        </details>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <input ref={refInput} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onAddReference(e.target.files[0])} data-testid="reference-upload-input" />
        {session.references.map((r) => <img key={r} src={fileUrl(r)} alt="Inspiratiefoto" className="h-14 w-14 object-cover rounded-lg border" />)}
        {session.references.length < 2 && (
          <button type="button" onClick={() => refInput.current?.click()} disabled={busy} className="inline-flex items-center gap-2 text-sm text-[#3a190b] border border-dashed border-[#3a190b]/40 rounded-lg px-3 py-2 hover:bg-[#f4efe9]" data-testid="add-reference-btn">
            <ImagePlus className="h-4 w-4" />Inspiratiefoto of moodboard toevoegen
          </button>
        )}
      </div>

      <button type="button" data-submit="true" disabled={disabled} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#3a190b] hover:bg-[#500000] text-white px-8 py-3.5 rounded-full font-semibold transition-colors disabled:opacity-60" data-testid="generate-btn">
        {pending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
        {pending ? 'Uw ontwerp wordt gemaakt (±1 min)…' : refining ? 'Nieuw ontwerp maken' : 'Toon mijn gerenoveerde ruimte'}
      </button>
      <p className="text-xs text-[#202020]/50" data-testid="remaining-text">{remaining} {remaining === 1 ? 'ontwerp' : 'ontwerpen'} beschikbaar in deze sessie. Gratis en vrijblijvend.</p>
    </SafeForm>
  );
};

export default DesignOptions;
